import React, { useState, useEffect, useRef } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Alert, Platform, Image, TextInput, ActivityIndicator } from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useThemeColors } from '../constants/theme';
import { CUOTA_TARJETA_ENABLED, COBROS_EN_LA_APP } from '../constants/featureFlags';
import * as Clipboard from 'expo-clipboard';
import * as ImagePicker from 'expo-image-picker';
import { RechargeService } from '../services/rechargeService';
import * as WebBrowser from 'expo-web-browser';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { callCrearPagoCuota, callConfirmarPagoCuota, callCrearRecargaTarjeta } from '../services/cloudFunctions';
import { useAppStore } from '../store/useAppStore';
import { bloquearSiInvitado } from '../services/modoInvitado';
import { db } from '../services/firebaseConfig';
import { doc, getDoc, onSnapshot, collection, query, where, limit, orderBy as fbOrderBy } from 'firebase/firestore';
// SDK NATIVO de Firestore. En Android el SDK web viaja SIN SESIÓN (el login
// real vive en @react-native-firebase vía authBridge), así que las reglas de
// /users, /recharge_requests y /driver_bonuses —todas comparan el uid del
// documento contra request.auth.uid— denegaban TODO en el teléfono. Por eso la
// billetera decía RD$0.00 y "todavía no hay movimientos" aunque el chofer
// acabara de depositar su cuota. Mismo patrón que services/firestoreService.ts.
import nativeFirestore from '@react-native-firebase/firestore';
import { paymentAccountsService, type PaymentAccount } from '../services/paymentAccountsService';
import { pesos } from '../utils/dinero';
import { mensajeDeError } from '../utils/mensajeDeError';

/**
 * ¿El fallo es "falta el índice compuesto"? Firestore lo reporta como
 * `failed-precondition` y el texto trae el enlace para crearlo.
 */
function faltaIndice(e: any): boolean {
  const codigo = String(e?.code ?? '').toLowerCase();
  const msg = String(e?.message ?? e ?? '').toLowerCase();
  return (
    codigo.includes('failed-precondition') ||
    msg.includes('failed-precondition') ||
    msg.includes('requires an index') ||
    msg.includes('requiere un índice') ||
    msg.includes('create_composite')
  );
}

/**
 * Escucha una colección filtrada por un campo, ORDENADA por `createdAt` desc,
 * con el SDK que corresponde a la plataforma. Devuelve la función para
 * cancelar la suscripción.
 *
 * POR QUÉ ORDENADA: `limit(N)` SIN `orderBy` no devuelve los N más recientes.
 * Firestore ordena por id de documento, que es aleatorio; en cuanto el chofer
 * pasa de N recargas deja de ver las últimas — justo las que viene a buscar.
 *
 * POR QUÉ HAY RESPALDO: la consulta ordenada exige un índice compuesto
 * declarado en firestore.indexes.json Y DESPLEGADO
 * (`firebase deploy --only firestore:indexes`). Mientras no lo esté, Firestore
 * contesta `failed-precondition` y el chofer vería "No pudimos cargar tus
 * movimientos" en lugar de su dinero. Reintentamos sin orden y ordenamos en
 * memoria: se pierde la garantía de "los N más recientes", nunca la lista.
 *
 * `alFallar` NO es opcional a propósito: el fallo tiene que llegar a la
 * pantalla. Un `console.warn` deja al chofer viendo una lista vacía, que él
 * lee como "mi dinero no llegó".
 */
function escucharPorCampo(
  coleccion: string,
  campo: string,
  valor: string,
  tope: number,
  alRecibir: (docs: any[]) => void,
  alFallar: (mensaje: string) => void
): () => void {
  let cancelada = false;
  let cancelar: (() => void) | null = null;

  // Un solo reintento. Con esta bandera es imposible entrar en bucle si el
  // respaldo también fallara con el mismo código.
  let reintentado = false;

  const manejarError = (conOrden: boolean, e: any) => {
    if (cancelada) return;
    if (conOrden && !reintentado && faltaIndice(e)) {
      reintentado = true;
      console.warn(`[wallet] ${coleccion}(${campo}+createdAt) sin índice desplegado: reintento sin orden`);
      const anterior = cancelar;
      cancelar = null;
      try { anterior?.(); } catch { /* ya estaba cerrada */ }
      const respaldo = suscribir(false);
      if (cancelada) { try { respaldo(); } catch { /* nada */ } }
      else cancelar = respaldo;
      return;
    }
    alFallar(String(e?.message ?? e));
  };

  const suscribir = (conOrden: boolean): (() => void) => {
    if (Platform.OS !== 'web') {
      let ref: any = nativeFirestore().collection(coleccion).where(campo, '==', valor);
      if (conOrden) ref = ref.orderBy('createdAt', 'desc');
      return ref.limit(tope).onSnapshot(
        (snap: any) => alRecibir(snap.docs.map((d: any) => ({ id: d.id, ...d.data() }))),
        (e: any) => manejarError(conOrden, e)
      ) as () => void;
    }
    const partes: any[] = [where(campo, '==', valor)];
    if (conOrden) partes.push(fbOrderBy('createdAt', 'desc'));
    partes.push(limit(tope));
    return onSnapshot(
      query(collection(db, coleccion), ...partes),
      (s) => alRecibir(s.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (e) => manejarError(conOrden, e)
    ) as () => void;
  };

  const inicial = suscribir(true);
  // Si el fallo llegara SÍNCRONO dentro de `suscribir(true)`, `manejarError` ya
  // dejó el respaldo en `cancelar`: pisarlo aquí lo dejaría huérfano y vivo.
  if (!reintentado && !cancelada) cancelar = inicial;
  else { try { inicial(); } catch { /* nada */ } }

  return () => {
    cancelada = true;
    try { cancelar?.(); } catch { /* ya estaba cerrada */ }
  };
}

/**
 * Roles que PAGAN membresía mensual para trabajar. Los mismos tres que usa el
 * servidor (`DRIVER_ROLES` en functions/src/loyalty.ts). Un pasajero, un dueño
 * de restaurante o un rent car NO pagan cuota: a ellos la palabra "membresía"
 * no puede ni aparecerles.
 */
const ROLES_CON_CUOTA = new Set(['DRIVER', 'DELIVERY_DRIVER', 'SCHOOL_DRIVER']);

/**
 * Redondeo a centavos. Con `Math.round(n)` a entero, la cifra de la pantalla
 * podia separarse hasta medio peso de la que calcula el servidor; en dinero,
 * "casi igual" es una cifra distinta.
 */
const aCentavos = (n: number): number => Math.round((Number(n) || 0) * 100) / 100;

/**
 * Cuota mensual base según el documento del usuario, ESPEJO EXACTO del cálculo
 * del servidor (`getMonthlyFee` + `applyMonthlyCharge` en
 * functions/src/loyalty.ts):
 *
 *     base = customMonthlyFee  ??  tabla(rol, vehículo)
 *
 * `customMonthlyFee` gana SIEMPRE, incluido el 0 (un mes de cortesía es un
 * valor válido, no un campo vacío). Por eso se comprueba `typeof === 'number'`
 * y no con `||`: `Number(null)` es 0 y habría convertido un campo ausente en
 * una cuota gratis.
 *
 * Devuelve null cuando el usuario no es chofer: no hay cuota que enseñar.
 */
function cuotaBaseDelDocumento(data: any, vehiculoVerificado?: string | null): number | null {
  const rol = typeof data?.role === 'string' ? data.role : null;
  if (!rol || !ROLES_CON_CUOTA.has(rol)) return null;

  const propia = data?.customMonthlyFee;
  if (typeof propia === 'number' && Number.isFinite(propia) && propia >= 0) return propia;

  // V25 — el MISMO vehículo que usa el servidor (`vehiculoDeLaCuota`): primero
  // el verificado en el KYC, que no puede tocar el teléfono; si no hay, el
  // suelto. Con el suelto a secas, quien se ponía "MOTORCYCLE" depositaba
  // RD$1,000 y el día 1 le cobraban RD$1,300.
  const vt = String(vehiculoVerificado || data?.vehicleType || '').trim().toUpperCase();
  if (rol === 'SCHOOL_DRIVER') return 2500;
  if (vt === 'MOTORCYCLE' || vt === 'BICYCLE') return 1000;
  if (vt === 'CAR') return 1300;
  if (vt === 'MINIBUS') return 1500;
  if (rol === 'DELIVERY_DRIVER') return 1000;
  return 1300;
}

// Estilo visual por tipo de cuenta. Vive fuera del componente: el color y el
// ícono son presentación, no datos — no tienen por qué estar en Firestore.
const KIND_STYLE: Record<PaymentAccount['kind'], { color: string; icon: string }> = {
  BANK: { color: '#004B8D', icon: 'business' },
  PHONE: { color: '#6C4CF1', icon: 'phone-portrait' },
  CASH: { color: '#2E7D32', icon: 'cash' },
};

/**
 * Rótulo REAL del dato que va debajo del número.
 *
 * Antes estaba escrito a mano como "Cuenta Corriente" para TODAS las cuentas,
 * aunque el dato trae su tipo (`accountType`: Ahorros / Corriente / Teléfono /
 * Efectivo). Un chofer que transfiere a una cuenta de ahorros creyendo que es
 * corriente —o que manda una transferencia bancaria a un número de teléfono de
 * Toke— pierde el dinero, y ese dinero es su cuota para poder trabajar.
 */
function etiquetaCuenta(a: PaymentAccount): string {
  const tipo = String(a.accountType ?? '').trim();
  if (a.kind === 'PHONE') return tipo || 'Teléfono';
  if (a.kind === 'CASH') return tipo || 'Efectivo';
  if (!tipo) return 'Número de cuenta';
  // "Cuenta de Ahorros" / "Cuenta Corriente" — como los rotula el banco.
  return /ahorro/i.test(tipo) ? 'Cuenta de Ahorros' : `Cuenta ${tipo}`;
}

/** Adapta el modelo de Firestore a la forma que ya espera el render. */
function toBankCard(a: PaymentAccount) {
  const style = KIND_STYLE[a.kind] ?? KIND_STYLE.BANK;
  return {
    id: a.id,
    name: a.name,
    accountName: a.holder,
    accountNumber: a.accountNumber,
    // El tipo REAL de la cuenta, ya rotulado. No se inventa.
    accountLabel: etiquetaCuenta(a),
    color: style.color,
    icon: style.icon,
  };
}

export default function WalletScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const userFirebaseUid = useAppStore((s) => s.userFirebaseUid);
  // EL NOMBRE QUE HAY QUE PONER COMO REFERENCIA EN LA TRANSFERENCIA.
  // Peticion del fundador (1-oct-2026): «que el chofer al momento de hacer la
  // transferencia solo incluya su nombre completo como referencia, para verlo
  // en el comprobante». Sin eso, al admin le cae un deposito en el estado de
  // cuenta del banco y no sabe de quien es: tiene que cruzarlo a mano contra la
  // foto del comprobante, y si el chofer puso otra cosa (o nada), no cruza.
  const nombreParaLaReferencia = useAppStore((s) => s.userName);

  const [viewState, setViewState] = useState<'HOME' | 'RECHARGE_SELECT' | 'UPLOAD_RECEIPT' | 'PENDING_VERIFICATION' | 'STRIPE_PROCESSING'>('HOME');
  // FIX AUDIT — Balance leído de Firestore en vivo. Antes era useState(0.00)
  // que nunca se reconciliaba con el saldo real del backend: el user recargaba
  // DOP 500, cerraba la app, volvía a abrirla y veía $0 otra vez. El monto
  // SÍ se guardaba server-side (las CFs de pagos lo escriben), pero el
  // cliente nunca lo leía. Onsnapshot mantiene el saldo actualizado en
  // tiempo real cuando la CF de pago confirma el ingreso.
  const [balance, setBalance] = useState(0.00);

  // ACLARACIÓN DEL FUNDADOR — Para un CHOFER, la "recarga" no es un top-up de
  // monto libre: es la vía para pagar su MEMBRESÍA/CUOTA MENSUAL por depósito o
  // transferencia cuando no tiene tarjeta.
  //
  // PERO ESO SOLO VALE PARA EL CHOFER. Antes esta pantalla arrancaba con
  // `membershipFee = 1300` y se lo enseñaba a TODO EL MUNDO: a un pasajero que
  // solo entraba a ver su saldo la app le exigía depositar RD$ 1,300 de
  // "membresía mensual para trabajar" — una cuota que no existe para él.
  //
  // Ahora el rol sale del DOCUMENTO de Firestore, nunca del almacén local:
  // `userRole` es lo que la persona eligió en la app y puede decir DRIVER en el
  // teléfono de un pasajero. La única fuente que manda es la misma que factura.
  //
  // null = todavía no lo sabemos (o no se pudo leer). Ante la duda NO se habla
  // de membresía: es preferible no decir nada que cobrar de mentira.
  const [rolDelDocumento, setRolDelDocumento] = useState<string | null>(null);
  // Cuota base del mes y crédito acumulado, tal como los guarda el servidor.
  // La cuota sale del documento Y del vehículo verificado del KYC (V25).
  const [docParaCuota, setDocParaCuota] = useState<any>(null);
  const [vehiculoVerificado, setVehiculoVerificado] = useState<string | null>(null);
  const cuotaBase = docParaCuota ? cuotaBaseDelDocumento(docParaCuota, vehiculoVerificado) : null;
  const [bloqueadoPorImpago, setBloqueadoPorImpago] = useState(false);
  const [creditoCuota, setCreditoCuota] = useState(0);
  /** Cargo del mes que el servidor ya cerró y va a cobrar. Manda sobre el cálculo. */
  const [cargoPendiente, setCargoPendiente] = useState<number | null>(null);

  const esChoferConCuota = !!rolDelDocumento && ROLES_CON_CUOTA.has(rolDelDocumento);

  // ─── LAS CIFRAS DEL MES, ESPEJO EXACTO DEL SERVIDOR ───────────────────────
  // `applyMonthlyCharge` y `resolveRechargeRequest` (vía `aplicarSaldosALaCuota`
  // en functions/src/loyalty.ts) hacen, en este orden:
  //     creditApplied  = min(subscriptionCredit, deuda)
  //     walletApplied  = min(walletBalance, deuda − creditApplied)
  //     lo que falta   = deuda − creditApplied − walletApplied
  // donde la deuda es el cargo abierto si lo hay, y si no la cuota del mes.
  // Se replica IGUAL. Si esta cuenta no fuera la misma, el chofer depositaría
  // una cifra y el servidor le cobraría otra — y quien pierde siempre es él.
  //
  // Son dos bolsas distintas y se enseñan por separado: `subscriptionCredit`
  // es el SALDO A FAVOR (lo que gana: compensaciones, bonos, crédito de
  // lealtad) y `walletBalance` es lo que ya depositó y todavía no se gastó en
  // una cuota. Las dos pagan, y en ese orden (V05).

  // ── EL CARGO ABIERTO MANDA SOBRE EL CÁLCULO ──────────────────────────────
  // A partir del día 1 el servidor cierra el mes: consume el crédito, apunta lo
  // que queda en `pendingCharge` y ESE es el número que va a cobrar. La
  // pantalla lo ignoraba y seguía calculando cuota − crédito por su cuenta, con
  // un crédito que el servidor ya había gastado. Resultado: le pedía al chofer
  // una cantidad distinta de la que de verdad debe, y el que depositaba lo que
  // decía la pantalla se quedaba corto o pagaba de más.
  //
  // Si hay cargo abierto, se enseña el cargo. Sin inventar nada: es el mismo
  // número que el servidor descuenta al aprobar el depósito.
  const cargoAbierto = cargoPendiente !== null ? aCentavos(cargoPendiente) : null;
  // Lo que se debe: el cargo abierto manda; si no hay, la cuota del mes.
  const deudaDelMes = cargoAbierto !== null ? cargoAbierto : cuotaBase;
  // V05 — el saldo a favor se aplica también contra el cargo abierto: el
  // servidor ya lo descuenta al aprobar el depósito.
  const saldoAplicado = deudaDelMes === null ? 0 : aCentavos(Math.min(creditoCuota, deudaDelMes));
  const billeteraAplicada =
    deudaDelMes === null ? 0 : aCentavos(Math.min(Math.max(0, balance), Math.max(0, deudaDelMes - saldoAplicado)));

  // Nunca negativo: el `max(0, …)` es el mismo piso que pone el servidor.
  const montoAPagar =
    deudaDelMes === null ? null : aCentavos(Math.max(0, deudaDelMes - saldoAplicado - billeteraAplicada));
  // El excedente NO se pierde: el servidor lo arrastra a los meses siguientes.
  const saldoSobrante = aCentavos(Math.max(0, creditoCuota - saldoAplicado));
  // Solo se enseña una cifra de membresía cuando hay una cifra que cobrar.
  const mostrarMembresia = esChoferConCuota && montoAPagar !== null && montoAPagar > 0;
  // Con TARJETA solo se paga lo que ya se DEBE: un cargo abierto o la pausa por
  // impago. Es la misma regla del servidor (crearPagoCuota): a quien está al
  // día la pantalla le enseña la cuota del mes para adelantarla por
  // transferencia, pero un toque en "Pagar con tarjeta" no puede cobrarle otra
  // vez el mes que ya pagó. Y Stripe no cobra menos de ~USD 0.50 (RD$50).
  const puedePagarConTarjeta =
    // ⚠️ En iOS, NUNCA. Ver COBROS_EN_LA_APP en constants/featureFlags.
    COBROS_EN_LA_APP &&
    CUOTA_TARJETA_ENABLED &&
    mostrarMembresia &&
    (cargoAbierto !== null || bloqueadoPorImpago) &&
    (montoAPagar ?? 0) >= 50;

  // Monto que declara quien NO paga cuota (pasajero, restaurante, rent car).
  // Es opcional: el servidor acepta `declaredAmount` nulo y el admin acredita
  // lo que muestre el comprobante.
  const [montoDeclarado, setMontoDeclarado] = useState('');
  const montoDeclaradoNum = (() => {
    const n = Number(String(montoDeclarado).replace(/[^\d.]/g, ''));
    return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
  })();

  // MOVIMIENTOS REALES: recargas por depósito + bonos ganados. Se leen de las
  // dos colecciones donde el servidor ya los venía guardando y se juntan en una
  // sola lista ordenada por fecha.
  const [recargas, setRecargas] = useState<any[]>([]);
  const [bonos, setBonos] = useState<any[]>([]);
  // Si la lectura falla no podemos decir "no hay movimientos": son cosas
  // distintas y confundirlas es lo que hace creer que el depósito se perdió.
  // Un error por colección: si fallara solo una, la otra no puede borrar el
  // aviso al llegar después.
  const [errorRecargas, setErrorRecargas] = useState<string | null>(null);
  const [errorBonos, setErrorBonos] = useState<string | null>(null);
  // Un "recibido" POR COLECCIÓN. Antes había uno solo y compartido: la primera
  // respuesta lo ponía en true, así que la pantalla dejaba de esperar a la otra.
  const [recargasListas, setRecargasListas] = useState(false);
  const [bonosListos, setBonosListos] = useState(false);
  // Pagos de la cuota con TARJETA ya acreditados (`wallet_topups`, los escribe
  // solo el servidor; las reglas dejan leer los propios).
  const [pagosTarjeta, setPagosTarjeta] = useState<any[]>([]);
  const [errorPagosTarjeta, setErrorPagosTarjeta] = useState<string | null>(null);
  const [pagosTarjetaListos, setPagosTarjetaListos] = useState(false);
  // ── COMPENSACIONES POR VIAJE CANCELADO ──────────────────────────────────
  // El fundador: «el saldo de los viajes cancelados por el usuario no se suma a
  // la cartera, se quedan pendiente abajo». Y era literal: ese abono SÍ se paga
  // —15% de la tarifa congelada, con tope por viaje y por mes
  // (functions/src/penalties.ts, `compensarAlChofer`)— pero cae en
  // `subscriptionCredit`, que REBAJA LA CUOTA y no es dinero retirable.
  // Esta pantalla escuchaba solo recargas, bonos y pagos con tarjeta, así que
  // el chofer veía su saldo a favor moverse SIN NINGUNA LÍNEA que dijera de
  // dónde venía. Un número que cambia solo se lee como un error de la app.
  const [compensaciones, setCompensaciones] = useState<any[]>([]);
  const [compensacionesListas, setCompensacionesListas] = useState(false);
  // Derivado, no un estado que haya que reiniciar dentro del efecto: sin sesión
  // no hay nada que esperar (si no, el invitado se quedaba con "Cargando…" para
  // siempre porque el efecto sale antes de suscribirse).
  const cargandoMovimientos =
    !!userFirebaseUid && !(recargasListas && bonosListos && pagosTarjetaListos && compensacionesListas);
  useEffect(() => {
    if (!userFirebaseUid) return;
    const unsubR = escucharPorCampo(
      'recharge_requests', 'userId', userFirebaseUid, 30,
      (docs) => { setRecargas(docs); setErrorRecargas(null); setRecargasListas(true); },
      (msg) => { console.warn('[wallet] recargas:', msg); setErrorRecargas(msg); setRecargasListas(true); }
    );
    const unsubB = escucharPorCampo(
      'driver_bonuses', 'driverUid', userFirebaseUid, 30,
      (docs) => { setBonos(docs); setErrorBonos(null); setBonosListos(true); },
      (msg) => { console.warn('[wallet] bonos:', msg); setErrorBonos(msg); setBonosListos(true); }
    );
    const unsubT = escucharPorCampo(
      'wallet_topups', 'uid', userFirebaseUid, 30,
      (docs) => { setPagosTarjeta(docs); setErrorPagosTarjeta(null); setPagosTarjetaListos(true); },
      (msg) => { console.warn('[wallet] pagos con tarjeta:', msg); setErrorPagosTarjeta(msg); setPagosTarjetaListos(true); }
    );
    const unsubC = escucharPorCampo(
      'driver_compensations', 'driverUid', userFirebaseUid, 30,
      (docs) => { setCompensaciones(docs); setCompensacionesListas(true); },
      (msg) => {
        // Si la regla no deja leer (cuenta que no es chofer), no es un error que
        // haya que enseñar: simplemente no hay compensaciones que mostrar.
        console.warn('[wallet] compensaciones:', msg);
        setCompensacionesListas(true);
      }
    );
    return () => { unsubR(); unsubB(); unsubT(); unsubC(); };
  }, [userFirebaseUid]);

  const fechaDe = (t: any) => {
    try { return t.toDate().toLocaleDateString('es-DO', { day: '2-digit', month: 'short' }); }
    catch { return '—'; }
  };
  const msDe = (t: any) => { try { return t.toMillis(); } catch { return 0; } };

  const movimientos = [
    // La compensación de un viaje que el PASAJERO canceló. Se marca aparte
    // porque NO es dinero de la billetera: rebaja la cuota del mes. Decirlo en
    // la propia línea evita la pregunta de «por qué no puedo retirarlo».
    ...compensaciones.map((c: any) => ({
      id: 'c' + c.id,
      tipo: 'COMPENSACION' as const,
      concepto: 'Te cancelaron un viaje',
      monto: Number(c.montoDop ?? 0) || 0,
      estado: 'APPROVED',
      estadoTexto: 'Va a tu cuota',
      fecha: fechaDe(c.creadoAt ?? c.createdAt ?? c.at),
      ms: msDe(c.creadoAt ?? c.createdAt ?? c.at),
    })),
    // DECIR A DÓNDE FUE EL DINERO, NO SOLO CUÁNTO ENTRÓ.
    //
    // Cuando el chofer debe cuota, el servidor coge del depósito lo que haga
    // falta para pagarla y solo el resto queda en la billetera: lo deja escrito
    // en `aplicadoACuota` y `cuotaPendienteTrasPago` (recargas.ts:552-554)
    // justamente «para que el chofer entienda su saldo». Esta lista no leía
    // ninguno de los dos y ponía «Recarga por depósito · Acreditada» con el
    // monto entero. El chofer depositaba 2.000, veía «acreditada 2.000», y en
    // la billetera encontraba 300. Nada estaba mal: el mensaje mentía.
    ...recargas.map((r: any) => {
      const aCuota = Number(r.aplicadoACuota ?? 0) || 0;
      const total = r.creditedAmount ?? r.ocrAmount ?? r.declaredAmount ?? 0;
      const aprobada = r.status === 'APPROVED';
      const enBilletera = Math.max(0, Number(total) - aCuota);
      return {
        id: 'r' + r.id,
        tipo: 'RECARGA' as const,
        concepto: aprobada && aCuota > 0 ? 'Depósito · pagó tu cuota' : 'Recarga por depósito',
        monto: total,
        estado: r.status,
        estadoTexto: !aprobada
          ? (r.status === 'PENDING_REVIEW' ? 'En revisión' : 'Rechazada')
          : aCuota > 0
            ? (enBilletera > 0
                ? `${pesos(aCuota)} a tu cuota · ${pesos(enBilletera)} a tu saldo`
                : 'Todo fue a tu cuota')
            : 'Acreditada',
        fecha: fechaDe(r.createdAt),
        ms: msDe(r.createdAt),
      };
    }),
    ...bonos.map((b: any) => ({
      id: 'b' + b.id,
      tipo: 'BONO' as const,
      concepto: b.concepto || 'Bono',
      monto: b.amount ?? 0,
      estado: b.status,
      estadoTexto:
        b.status === 'ACREDITADO' ? 'Abonado a tu saldo'
        : b.status === 'TRANSFERIDO' ? 'Transferido a tu cuenta'
        : 'Pendiente de transferir',
      fecha: fechaDe(b.createdAt),
      ms: msDe(b.createdAt),
    })),
    ...pagosTarjeta.map((t: any) => ({
      id: 't' + t.id,
      tipo: 'TARJETA' as const,
      // Se ramifica primero por TIPO y no por `aplicadoACuota`. Una recarga
      // trae `aplicadoACuota: 0` por diseño, así que caía siempre en el texto
      // de la cuota y el chofer leía «Pago con tarjeta (en tu billetera hasta
      // el día 1)» sobre un dinero que puede gastar ya. Los movimientos
      // antiguos no traen `tipo` y siguen por la rama de siempre, que para
      // ellos es la correcta.
      concepto:
        t.tipo === 'RECARGA_BILLETERA'
          ? 'Recarga con tarjeta'
          : Number(t.aplicadoACuota ?? 0) > 0
            ? 'Cuota pagada con tarjeta'
            : 'Pago con tarjeta (en tu billetera hasta el día 1)',
      // El NETO, no el bruto. Si hubo reembolso o contracargo, `revisarReversiones`
      // escribe `montoRevertido` en este mismo documento pero NO toca `estado`:
      // el admin veía el neto y el chofer seguía viendo el monto entero como
      // «Acreditada» sobre un dinero que ya se le devolvió.
      monto: Math.max(0, Number(t.monto ?? 0) - Number(t.montoRevertido ?? 0)),
      estado: t.estado,
      estadoTexto:
        Number(t.montoRevertido ?? 0) > 0
          ? (Number(t.montoRevertido ?? 0) >= Number(t.monto ?? 0) - 0.005 ? 'Devuelta' : 'Devuelta en parte')
          : t.estado === 'ACREDITADO'
            ? 'Acreditada'
            : t.estado === 'SIN_USUARIO'
              ? 'Pendiente de devolución'
              : 'En revisión',
      fecha: fechaDe(t.createdAt),
      ms: msDe(t.createdAt),
    })),
  ].sort((a, b) => b.ms - a.ms);

  // Un RD$0.00 falso es peor que no mostrar nada: el chofer que sí depositó
  // asume que le robaron. Cuando la lectura falla lo decimos con todas las
  // letras en vez de pintar un cero.
  const [errorSaldo, setErrorSaldo] = useState<string | null>(null);
  const [saldoRecibido, setSaldoRecibido] = useState(false);
  // Igual que arriba: derivado, para que sin sesión no se quede "Cargando…".
  const saldoListo = !userFirebaseUid || saldoRecibido;
  useEffect(() => {
    if (!userFirebaseUid) return;

    const aplicar = (data: any) => {
      // El campo canonical es walletBalance (lo escribe el backend al
      // confirmar PaymentIntents y al cobrar cuota mensual del chofer).
      const fresh = Number(data?.walletBalance ?? 0);
      if (!Number.isNaN(fresh)) setBalance(fresh);

      // EL ROL SALE DEL DOCUMENTO, y de ningún otro sitio. Antes había un
      // `?? userRole` (el rol del almacén local) que traía de vuelta el
      // problema entero: bastaba con que el documento no trajera `role` para
      // que a un pasajero se le calculara —y se le exigiera— una cuota.
      const rol = typeof data?.role === 'string' ? data.role : null;
      setRolDelDocumento(rol);
      setDocParaCuota(data);
      setBloqueadoPorImpago(data?.driverBlockedNoPayment === true);

      // Crédito acumulado a favor del chofer. El servidor lo descuenta de la
      // cuota antes de cobrar; la pantalla tiene que pedir lo mismo.
      const credito = data?.subscriptionCredit;
      setCreditoCuota(typeof credito === 'number' && Number.isFinite(credito) && credito > 0 ? credito : 0);
      const cargo = Number((data as any)?.pendingCharge?.amount);
      setCargoPendiente(Number.isFinite(cargo) && cargo > 0 ? cargo : null);

      setErrorSaldo(null);
      setSaldoRecibido(true);
    };
    const fallo = (msg: string) => {
      console.warn('[wallet] no se pudo leer saldo:', msg);
      // Si no pudimos leer el documento tampoco sabemos si es chofer ni cuánto
      // debe. Se limpia todo: enseñar la última cuota conocida —o peor, la de
      // por defecto— sería inventarle una deuda.
      setRolDelDocumento(null);
      setDocParaCuota(null);
      setBloqueadoPorImpago(false);
      setCreditoCuota(0);
      setCargoPendiente(null);
      setErrorSaldo(msg);
      setSaldoRecibido(true);
    };

    // RAMA NATIVA obligatoria: /users/{uid} exige isSelf(uid) y el SDK web
    // llega anónimo en Android, así que el saldo real nunca se leía.
    if (Platform.OS !== 'web') {
      const unsubNat = nativeFirestore()
        .collection('users')
        .doc(userFirebaseUid)
        .onSnapshot(
          // En @react-native-firebase v24 `exists` es MÉTODO, no propiedad:
          // leerlo como booleano daría siempre true y `data()` sería undefined.
          (snap: any) => aplicar(snap?.exists?.() ? snap.data() : null),
          (e: any) => fallo(String(e?.message ?? e))
        );
      return () => unsubNat();
    }

    const unsub = onSnapshot(
      doc(db, 'users', userFirebaseUid),
      (snap) => aplicar(snap.exists() ? snap.data() : null),
      (err) => fallo(String(err?.message ?? err))
    );
    return () => unsub();
    // No hay más dependencias: este efecto ya NO mira el rol del almacén local.
    // Todo lo que decide qué se le cobra al usuario sale del documento.
  }, [userFirebaseUid]);

  // V25 — el vehículo VERIFICADO (`kyc_submissions/{uid}.vehicleTypeVerificado`,
  // solo lo escribe el servidor al aprobar el KYC). Las reglas dejan leerlo al
  // dueño. Si la lectura falla o no existe, se queda en null y se usa el
  // suelto: exactamente lo que hace el servidor.
  useEffect(() => {
    if (!userFirebaseUid) { setVehiculoVerificado(null); return; }
    let vivo = true;
    const tomar = (data: any) => {
      const v = String(data?.vehicleTypeVerificado ?? '').trim().toUpperCase();
      if (vivo) setVehiculoVerificado(v || null);
    };
    const leer = async () => {
      try {
        if (Platform.OS !== 'web') {
          const snap: any = await nativeFirestore().collection('kyc_submissions').doc(userFirebaseUid).get();
          tomar(snap?.exists?.() ? snap.data() : null);
        } else {
          const snap = await getDoc(doc(db, 'kyc_submissions', userFirebaseUid));
          tomar(snap.exists() ? snap.data() : null);
        }
      } catch (e: any) {
        console.warn('[wallet] no se pudo leer el vehículo verificado:', e?.message ?? e);
        if (vivo) setVehiculoVerificado(null);
      }
    };
    leer();
    return () => { vivo = false; };
  }, [userFirebaseUid]);

  const [selectedBank, setSelectedBank] = useState<string | null>(null);

  // Comprobante de transferencia elegido. Solo la URI: `RechargeService.
  // submitReceipt` sube el archivo a Storage desde la URI y NO usa base64 —
  // el backend que corría OCR sobre base64 está borrado.
  const [receipt, setReceipt] = useState<{ uri: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // ─── LA CUOTA CON TARJETA ────────────────────────────────────────────────
  // Antes esto le pedía el cobro a Railway (borrado) y abría la hoja de pago
  // nativa de Stripe: el botón estaba muerto. Ahora el SERVIDOR calcula cuánto
  // debe el chofer y abre un cobro por la extensión de Stripe
  // (functions/src/pagoCuotaStripe.ts); aquí solo se abre esa página. La app NO
  // toca el saldo: lo acredita el servidor cuando Stripe confirma, y el listener
  // de /users/{uid} lo trae solo.
  // Montada / cancelada: si el chofer se va de la pantalla (o cancela) mientras
  // se prepara el cobro, NO se abre después la página de Stripe encima de otra
  // pantalla ni sale una alerta de una pantalla que ya no existe.
  const montada = useRef(true);
  const cobroCancelado = useRef(false);
  useEffect(() => () => { montada.current = false; }, []);

  /**
   * Pregunta al SERVIDOR cómo quedó el cobro (confirmarPagoCuota): él lo mira
   * en Stripe y, si está pagado, lo acredita en el acto. La URL de vuelta
   * (?pago=ok) NO es prueba de pago: una pestaña vieja o un arranque en frío
   * pueden traerla sin que nadie haya pagado ahora. Hasta 3 intentos, porque
   * Stripe puede tardar unos segundos en marcar la sesión como pagada.
   */
  const confirmarConElServidor = async (sesionId?: string, venimosDeOk = false) => {
    let estado = 'PENDIENTE';
    let monto: number | undefined;
    let respondio = false;
    for (let intento = 0; intento < 3; intento++) {
      try {
        const { data } = await callConfirmarPagoCuota(sesionId ? { sesionId } : {});
        respondio = true;
        estado = String(data?.estado ?? 'PENDIENTE');
        monto = typeof data?.monto === 'number' ? data.monto : undefined;
      } catch (e: any) {
        console.warn('[wallet] confirmarPagoCuota:', e?.message ?? e);
      }
      if (estado !== 'PENDIENTE') break;
      await new Promise((ok) => setTimeout(ok, 2500));
    }
    if (!montada.current) return;
    if (!respondio) {
      // Sin respuesta del servidor no sabemos si se cobró: decir "no se hizo
      // ningún cobro" podría llevarle a pagar dos veces.
      Alert.alert('No pudimos confirmar tu pago', 'Revisa tu conexión. Si llegaste a pagar, se acredita solo en unos minutos y te llega un aviso: no vuelvas a pagar. Si no pagaste, puedes intentarlo otra vez.');
      return;
    }
    if (estado === 'ACREDITADO') {
      Alert.alert('Pago acreditado', monto ? `Recibimos tu pago de ${pesos(monto)}. Tu cuota está al día.` : 'Recibimos tu pago. Tu cuota está al día.');
    } else if (estado === 'PENDIENTE' && venimosDeOk) {
      Alert.alert('Confirmando tu pago', 'Stripe todavía no lo confirma. En cuanto lo haga se acredita solo y te llega un aviso. No vuelvas a pagar.');
    } else if (estado === 'PENDIENTE' || estado === 'VENCIDO' || estado === 'SIN_COBRO') {
      Alert.alert('No se hizo ningún cobro', 'El pago no se completó. Puedes intentarlo otra vez o pagar por transferencia.');
    } else {
      Alert.alert('Revisamos tu pago', 'Tu pago necesita una revisión. Si se cobró, te lo acreditamos; escríbenos por soporte si tienes dudas.');
    }
  };

  // ARRANQUE EN FRÍO al volver de Stripe: Android pudo cerrar la app mientras
  // el chofer pagaba, y la vuelta la abre de cero en /wallet?pago=… sin la
  // promesa de openAuthSessionAsync. Se confirma igual con el servidor.
  const { pago: pagoDeVuelta } = useLocalSearchParams<{ pago?: string }>();
  const vueltaAtendida = useRef<string | null>(null);
  useEffect(() => {
    if (!userFirebaseUid || !pagoDeVuelta || Platform.OS === 'web') return;
    if (viewState === 'STRIPE_PROCESSING') return; // la atiende handleStripePayment
    if (vueltaAtendida.current === pagoDeVuelta) return;
    vueltaAtendida.current = pagoDeVuelta;
    (async () => {
      // ¿Esta vuelta es de una RECARGA? Si la marca está y es reciente, no se
      // le pregunta al servidor por una cuota que nadie abrió: esa consulta es
      // la que devuelve «no se hizo ningún cobro» sobre un cobro que sí ocurrió.
      let esRecarga = false;
      try {
        const marca = await AsyncStorage.getItem('gs_vuelta_recarga');
        const ms = Number(marca ?? 0);
        esRecarga = Number.isFinite(ms) && ms > 0 && Date.now() - ms < 45 * 60 * 1000;
        if (marca) await AsyncStorage.removeItem('gs_vuelta_recarga');
      } catch { /* sin disco: se sigue por el camino de siempre */ }

      if (esRecarga) {
        if (pagoDeVuelta === 'ok') {
          Alert.alert(
            'Pago enviado',
            'Si el cobro salió bien, tu saldo se actualiza en unos segundos. No hace falta que hagas nada más.'
          );
        }
        return;
      }
      confirmarConElServidor(undefined, pagoDeVuelta === 'ok');
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userFirebaseUid, pagoDeVuelta]);

  // ─── LA CUOTA CON TARJETA ────────────────────────────────────────────────
  // Antes esto le pedía el cobro a Railway (borrado) y abría la hoja de pago
  // nativa de Stripe: el botón estaba muerto. Ahora el SERVIDOR calcula cuánto
  // debe el chofer y abre un cobro por la extensión de Stripe
  // (functions/src/pagoCuotaStripe.ts); aquí solo se abre esa página. La app NO
  // toca el saldo: lo acredita el servidor cuando Stripe confirma.
  /**
   * RECARGAR LA BILLETERA CON TARJETA (27-sep-2026).
   *
   * El fundador entró a «Recargar» y solo vio transferencia: la tarjeta hasta
   * hoy solo pagaba la CUOTA, y solo si había un cargo abierto. Esto es una
   * recarga de verdad, de monto libre desde RD$50 — que es, palabra suya, lo
   * que necesitaba para poder probar el cobro con su tarjeta.
   *
   * El monto lo valida el SERVIDOR (`crearRecargaTarjeta`): aquí solo se
   * propone. Y el abono es idempotente por el id del cobro de Stripe, igual
   * que el pago de cuota.
   */
  const [montoRecarga, setMontoRecarga] = useState('50');
  const [abriendoRecarga, setAbriendoRecarga] = useState(false);

  const handleRecargaConTarjeta = async () => {
    if (bloquearSiInvitado('recargar tu billetera')) return;
    const monto = Math.round(Number(String(montoRecarga).replace(/[^\d]/g, '')));
    if (!Number.isFinite(monto) || monto < 50) {
      Alert.alert('Monto muy bajo', 'La recarga mínima con tarjeta es de RD$50.');
      return;
    }
    setAbriendoRecarga(true);
    try {
      const r = await callCrearRecargaTarjeta({ monto });
      const url = String((r as any)?.data?.url ?? '');
      // La MISMA comprobación que el pago de cuota: solo se abre lo que venga
      // de Stripe. Si algún día la extensión devolviera otra cosa, no se abre.
      if (!url.startsWith('https://checkout.stripe.com/')) {
        throw new Error('El enlace de pago no vino de Stripe.');
      }
      // LA VUELTA DE UNA RECARGA NO ES LA VUELTA DE UNA CUOTA, Y COMPARTEN URL.
      // `success_url` es la misma página `/pago-cuota/listo`, que devuelve a
      // `gosafelyapp://wallet?pago=ok`. Si Android mata la app mientras el
      // chofer paga —teléfono de gama baja, que es el parque real—, la vuelta
      // arranca EN FRÍO, el efecto de `pagoDeVuelta` llama a
      // `confirmarPagoCuota`, que no sabe nada de recargas, y el chofer que
      // ACABA DE PAGAR puede leer «no se hizo ningún cobro».
      // Un ref no sirve aquí: en arranque en frío nace vacío. Se deja la marca
      // en disco, que es lo único que sobrevive a que el sistema cierre la app.
      await AsyncStorage.setItem('gs_vuelta_recarga', String(Date.now())).catch(() => {});
      await WebBrowser.openAuthSessionAsync(url, 'gosafelyapp://wallet');
      // Al volver no se promete nada: el saldo se refresca solo cuando el
      // servidor acredita (webhook o conciliador). Decir «ya está» antes de
      // que Stripe confirme sería mentir.
      Alert.alert(
        'Pago enviado',
        'Si el cobro salió bien, tu saldo se actualiza en unos segundos. No hace falta que hagas nada más.'
      );
    } catch (e: any) {
      Alert.alert('No pudimos abrir el pago', e?.message ?? 'Revisa tu conexión e intenta de nuevo.');
    } finally {
      setAbriendoRecarga(false);
    }
  };

  const handleStripePayment = async () => {
    if (bloquearSiInvitado('pagar tu cuota')) return;
    if (!userFirebaseUid) {
      Alert.alert('Sesión requerida', 'Inicia sesión para pagar tu cuota.');
      return;
    }
    cobroCancelado.current = false;
    setViewState('STRIPE_PROCESSING');
    try {
      const { data } = await callCrearPagoCuota({});
      const url = String(data?.url ?? '');
      const sesionId = data?.sesionId ? String(data.sesionId) : undefined;
      // Solo se abre la página de pago de Stripe, nunca otra dirección.
      if (!url.startsWith('https://checkout.stripe.com/')) {
        throw new Error('No pudimos abrir el pago con tarjeta. Intenta de nuevo o paga por transferencia.');
      }
      if (!montada.current || cobroCancelado.current) return;
      if (Platform.OS === 'web') {
        window.location.assign(url);
        return;
      }
      // La página de vuelta (public/pago-cuota) devuelve a la app con
      // gosafelyapp://wallet?pago=ok|cancelado; al llegar, esta pestaña se cierra.
      const r = await WebBrowser.openAuthSessionAsync(url, 'gosafelyapp://wallet');
      if (!montada.current) return;
      setViewState('HOME');
      const vuelta = r.type === 'success' ? String((r as any).url ?? '') : '';
      // Esta vuelta ya queda atendida aquí: el efecto de arranque en frío no
      // debe repetir la confirmación.
      if (vuelta.includes('pago=')) vueltaAtendida.current = vuelta.includes('pago=ok') ? 'ok' : 'cancelado';
      if (vuelta.includes('pago=cancelado')) {
        Alert.alert('No se hizo ningún cobro', 'Cerraste el pago antes de terminarlo. Puedes intentarlo otra vez o pagar por transferencia.');
        return;
      }
      // Volvió por el botón de "Pago recibido" o cerró la pestaña a mano: en
      // los dos casos manda lo que diga el servidor, no la URL.
      await confirmarConElServidor(sesionId, vuelta.includes('pago=ok'));
    } catch (err: any) {
      if (!montada.current) return;
      setViewState('HOME');
      if (cobroCancelado.current) return;
      Alert.alert(
        'No pudimos abrir el pago',
        mensajeDeError(err, 'No pudimos abrir el pago con tarjeta. Intenta de nuevo en unos minutos o paga por transferencia.')
      );
    }
  };

  // ─── CUENTAS DE COBRO ────────────────────────────────────────────────
  // Ya NO están incrustadas aquí. Viven en Firestore, documento
  // `app_config/payment_accounts`, y se editan desde la consola de Firebase
  // sin recompilar ni volver a subir la app a Play Store.
  //
  // Antes había una cuenta a nombre de un TERCERO ajeno al negocio — Google
  // Play lo trata como señal de fraude, y expone legalmente al usuario que
  // transfiere. Con esto, cambiar o retirar una cuenta es inmediato.
  //
  // Arranca con el respaldo (render instantáneo, sin parpadeo) y se
  // reemplaza en cuanto Firestore responde.
  const [BANKS, setBANKS] = useState(() => paymentAccountsService.getFallback().map(toBankCard));
  useEffect(() => {
    let alive = true;
    paymentAccountsService
      .getAccounts()
      .then((accounts) => {
        // Se acepta la lista TAL CUAL, incluso vacía. Antes se ignoraba el
        // vacío y quedaba en pantalla el respaldo incrustado: el usuario
        // transfería a una cuenta ya retirada y el servidor le rechazaba el
        // comprobante con el dinero ya fuera de su banco.
        if (alive) setBANKS(accounts.map(toBankCard));
      })
      .catch(() => { /* el respaldo ya está en pantalla */ });
    return () => { alive = false; };
  }, []);

  // El aviso nombra lo que REALMENTE se copió. Decir "número de cuenta de
  // Toke" cuando lo copiado es un número de teléfono confunde al chofer sobre
  // qué tipo de envío tiene que hacer.
  const handleCopy = async (text: string, bankName: string, etiqueta: string) => {
    const que = `${etiqueta} de ${bankName}`;
    if (Platform.OS !== 'web') {
      await Clipboard.setStringAsync(text);
      Alert.alert('Copiado', `${que} copiado al portapapeles.`);
    } else {
      navigator.clipboard.writeText(text);
      window.alert(`${que} copiado.`);
    }
  };

  // Elegir el comprobante: cámara o galería (la mayoría son capturas de
  // pantalla de la app del banco, por eso ofrecemos ambas).
  const pickFrom = async (source: 'camera' | 'library') => {
    try {
      // SIN `base64: true`: nadie lo usaba. Pedirlo obliga al selector a
      // cargar la foto entera en memoria como texto (~33% más grande) antes de
      // devolverla — en un teléfono de gama baja eso es el cierre de la app
      // justo al elegir el comprobante. Se sube por URI.
      const opts: ImagePicker.ImagePickerOptions = {
        quality: 0.7,
        mediaTypes: ['images'],
      };
      let result;
      if (source === 'camera') {
        const perm = await ImagePicker.requestCameraPermissionsAsync();
        if (!perm.granted) {
          Alert.alert('Permiso de cámara', 'Necesitamos acceso a la cámara para tomar la foto del comprobante.');
          return;
        }
        result = await ImagePicker.launchCameraAsync(opts);
      } else {
        result = await ImagePicker.launchImageLibraryAsync(opts);
      }
      if (result.canceled || !result.assets?.[0]?.uri) return;
      setReceipt({ uri: result.assets[0].uri });
    } catch (e) {
      console.warn('[wallet] pickReceipt fallo:', e);
      Alert.alert('Error', 'No pudimos abrir la imagen. Intenta de nuevo.');
    }
  };

  const pickReceipt = () => {
    if (Platform.OS === 'web') {
      pickFrom('library');
      return;
    }
    Alert.alert('Subir comprobante', '¿Cómo quieres agregar la captura de tu transferencia?', [
      { text: 'Tomar foto', onPress: () => pickFrom('camera') },
      { text: 'Elegir de galería', onPress: () => pickFrom('library') },
      { text: 'Cancelar', style: 'cancel' },
    ]);
  };

  const handleSubmitReceipt = async () => {
    if (!receipt) {
      Alert.alert('Falta el comprobante', 'Primero sube la captura de tu transferencia.');
      return;
    }
    if (!selectedBank) {
      Alert.alert('Elige el banco', 'Vuelve y selecciona a qué cuenta transferiste.');
      return;
    }
    if (!userFirebaseUid) {
      Alert.alert('Sesión requerida', 'Inicia sesión para recargar tu billetera.');
      return;
    }
    setSubmitting(true);
    try {
      // Se manda la URI de la foto (ya no base64): el servicio la sube a
      // Storage y pasa la URL a la Cloud Function. El backend de Railway que
      // recibía base64 y corría OCR está borrado.
      // El monto declarado es el que le pedimos depositar (chofer) o el que él
      // mismo escribió. `undefined` es válido: el servidor lo acepta nulo.
      const declarado = mostrarMembresia ? montoAPagar! : (montoDeclaradoNum ?? undefined);
      const res = await RechargeService.submitReceipt(
        receipt.uri,
        userFirebaseUid,
        selectedBank,
        declarado
      );
      if (res.status === 'APPROVED') {
        // El saldo se refresca solo por el listener de Firestore (useEffect).
        setReceipt(null);
        setMontoDeclarado('');
        Alert.alert('¡Recarga acreditada!', res.message, [
          { text: 'Genial', onPress: () => setViewState('HOME') },
        ]);
      } else if (res.status === 'PENDING_REVIEW') {
        setReceipt(null);
        setMontoDeclarado('');
        setViewState('PENDING_VERIFICATION');
      } else if (res.status === 'REJECTED_DUPLICATE') {
        Alert.alert('Comprobante ya usado', res.message);
      } else {
        Alert.alert('No pudimos procesar tu recarga', res.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const renderHome = () => (
    <ScrollView contentContainerStyle={styles.scrollContent}>
      {/* Tarjeta de Balance */}
      <View style={[styles.balanceCard, { backgroundColor: colors.card }]}>
        {/* El rótulo dice QUÉ es la cifra. Para el chofer la cifra que le
            importa —y la única que le rebaja la membresía— es su saldo a
            favor; el depositado se le muestra aparte, más abajo. */}
        <Text style={styles.balanceLabel}>
          {esChoferConCuota ? 'Saldo a favor' : 'Saldo Disponible'}
        </Text>
        {/* Cuatro estados distintos, nunca uno disfrazado de otro: sin sesión,
            cargando, no se pudo leer, y el saldo real.
            Sin sesión NO hay saldo que leer, así que "RD$ 0.00" no era un dato:
            era un cero con pinta de dato en la pantalla del dinero. */}
        {!userFirebaseUid ? (
          <>
            <Text style={[styles.balanceAmount, { color: colors.textSecondary, fontSize: 20, marginBottom: 6 }]}>
              Sin cuenta todavía
            </Text>
            <Text style={{ color: colors.textSecondary, fontSize: 12, textAlign: 'center', marginBottom: 14 }}>
              Estás explorando sin cuenta. Cuando entres con la tuya, aquí verás tu saldo
              real y tus movimientos.
            </Text>
          </>
        ) : !saldoListo ? (
          <Text style={[styles.balanceAmount, { color: colors.textSecondary, fontSize: 24 }]}>
            Cargando…
          </Text>
        ) : errorSaldo ? (
          <>
            <Text style={[styles.balanceAmount, { color: '#D32F2F', fontSize: 22, marginBottom: 6 }]}>
              No pudimos leer tu saldo
            </Text>
            <Text style={{ color: colors.textSecondary, fontSize: 12, textAlign: 'center', marginBottom: 14 }}>
              Revisa tu conexión y vuelve a entrar. Tu dinero está seguro: esto solo
              impide mostrarlo ahora mismo.
            </Text>
          </>
        ) : esChoferConCuota ? (
          /* DOS BOLSAS, DOS CIFRAS, Y NO SON LO MISMO.
             Todo lo que el chofer GANA (compensación por cancelación, bonos,
             crédito de lealtad) vive en `subscriptionCredit` y es lo único que
             le rebaja la cuota. `walletBalance` es lo que YA depositó. Antes
             ambas se pintaban bajo el mismo rótulo "Saldo Disponible" y ninguna
             descontaba nada: el chofer ganaba RD$600 y seguía viendo que debía
             la cuota entera. */
          <>
            <Text style={[styles.balanceAmount, { color: colors.primary }]}>
              {pesos(creditoCuota)}
            </Text>
            <Text style={{ color: colors.textSecondary, fontSize: 12, textAlign: 'center', marginBottom: 10 }}>
              {creditoCuota > 0
                ? 'Se descuenta de tu membresía antes de que deposites nada. Lo que sobre queda para el mes siguiente.'
                : 'Aquí caen tus bonos y las compensaciones por cancelación, y te rebajan la membresía del mes.'}
            </Text>
            {balance > 0 && (
              <Text style={{ color: colors.textSecondary, fontSize: 12, textAlign: 'center', marginBottom: 10 }}>
                Además llevas {pesos(balance)} depositados y acreditados en tu cuenta.
              </Text>
            )}
          </>
        ) : (
          <Text style={[styles.balanceAmount, { color: colors.primary }]}>
            {pesos(balance)}
          </Text>
        )}
        {/* ══════════════════════════════════════════════════════════════
            EN iOS NO HAY PUERTA DE PAGO. NI UNA.
            ══════════════════════════════════════════════════════════════
            Este botón es la entrada a RECHARGE_SELECT, donde viven los
            números de cuenta bancaria reales, la cifra exacta a depositar y
            el pago con tarjeta por Stripe. La regla 3.1.1 de la App Store
            prohíbe expresamente «botones, enlaces externos u otras llamadas a
            la acción que dirijan a formas de pago distintas» de la de Apple
            cuando lo que se compra desbloquea funciones de la app — y la
            membresía del chofer es un booleano que hace justo eso.

            En iPhone se sustituye por una frase que solo INFORMA: sin precio,
            sin botón, sin cuentas y sin enlace a ninguna parte. Es lo mismo
            que hacen Netflix o Spotify al amparo de la 3.1.3(f).

            En Android y en web esto no cambia en nada. */}
        {!COBROS_EN_LA_APP ? (
          <Text
            style={{
              color: colors.textSecondary,
              fontSize: 13,
              textAlign: 'center',
              lineHeight: 19,
              marginTop: 4,
            }}
          >
            Tu saldo y tu membresía se gestionan desde tu cuenta de Go Safely.
          </Text>
        ) : (
        <View style={styles.balanceActions}>
          <TouchableOpacity
            style={[styles.actionBtn, { backgroundColor: colors.accent }]}
            /* EL FRENO VA AQUÍ, ANTES de enseñar nada.
               Antes el invitado entraba libremente a la lista de cuentas, veía
               los NÚMEROS DE CUENTA BANCARIOS REALES, transfería su dinero de
               verdad, y solo al subir el comprobante se topaba con "necesitas
               una cuenta". Es decir: se le bloqueaba después de haber pagado.
               `bloquearSiInvitado` es la misma puerta que ya usaba el botón de
               pagar con tarjeta; lo que faltaba era ponerla en la entrada. */
            onPress={() => {
              if (bloquearSiInvitado('recargar tu billetera')) return;
              setViewState('RECHARGE_SELECT');
            }}
          >
            <Ionicons name="add-circle" size={20} color="#FFF" />
            <Text style={styles.actionBtnText}>Recargar</Text>
          </TouchableOpacity>
        </View>
        )}
      </View>

      <Text style={[styles.sectionTitle, { color: colors.text }]}>Movimientos recientes</Text>

      {/* Antes este bloque estaba escrito a mano: SIEMPRE decía "Aún no tienes
          movimientos", aunque el chofer hubiera depositado RD$1,300. No existía
          ni una consulta. Para alguien que acaba de mandar su dinero, ver eso es
          creer que se perdió. Ahora se leen sus recargas y sus bonos reales. */}
      {/* AVISO POR COLECCIÓN, y la lista SIEMPRE debajo.
          Antes un fallo en cualquiera de las dos consultas reemplazaba el bloque
          entero: si fallaban los bonos, las recargas que SÍ habían llegado
          desaparecían de la pantalla y el chofer no veía su depósito. Ahora se
          pinta lo que llegó y se dice, con nombre, qué es lo que falta. */}
      {errorRecargas && (
        <View style={[styles.avisoFallo, { borderColor: '#D32F2F', backgroundColor: colors.card }]}>
          <Ionicons name="cloud-offline-outline" size={20} color="#D32F2F" />
          <Text style={{ flex: 1, marginLeft: 10, fontSize: 12, color: colors.textSecondary, lineHeight: 17 }}>
            <Text style={{ color: '#D32F2F', fontWeight: '700' }}>No pudimos cargar tus recargas. </Text>
            Esto NO significa que no tengas ninguna: tu depósito sigue registrado. Revisa
            tu conexión y vuelve a abrir la billetera.
          </Text>
        </View>
      )}
      {errorBonos && (
        <View style={[styles.avisoFallo, { borderColor: '#D32F2F', backgroundColor: colors.card }]}>
          <Ionicons name="cloud-offline-outline" size={20} color="#D32F2F" />
          <Text style={{ flex: 1, marginLeft: 10, fontSize: 12, color: colors.textSecondary, lineHeight: 17 }}>
            <Text style={{ color: '#D32F2F', fontWeight: '700' }}>No pudimos cargar tus bonos. </Text>
            Los bonos que hayas ganado siguen guardados. Revisa tu conexión y vuelve a
            abrir la billetera.
          </Text>
        </View>
      )}

      {errorPagosTarjeta && (
        <View style={[styles.avisoFallo, { borderColor: '#D32F2F', backgroundColor: colors.card }]}>
          <Ionicons name="cloud-offline-outline" size={20} color="#D32F2F" />
          <Text style={{ flex: 1, marginLeft: 10, fontSize: 12, color: colors.textSecondary, lineHeight: 17 }}>
            <Text style={{ color: '#D32F2F', fontWeight: '700' }}>No pudimos cargar tus pagos con tarjeta. </Text>
            Los pagos que hiciste siguen registrados. Revisa tu conexión y vuelve a
            abrir la billetera.
          </Text>
        </View>
      )}

      {!userFirebaseUid ? (
        // Sin sesión no se consultó nada, así que decir "todavía no hay
        // movimientos" sería una afirmación sobre una cuenta que no existe.
        <View style={styles.emptyState}>
          <Ionicons name="person-add-outline" size={48} color={colors.textSecondary} />
          <Text style={[styles.emptyStateText, { color: colors.textSecondary }]}>
            Tus recargas y bonos aparecerán aquí cuando tengas tu cuenta.
          </Text>
        </View>
      ) : cargandoMovimientos ? (
        <View style={styles.emptyState}>
          <Text style={[styles.emptyStateText, { color: colors.textSecondary }]}>
            Cargando tus movimientos…
          </Text>
        </View>
      ) : movimientos.length === 0 ? (
        errorRecargas || errorBonos || errorPagosTarjeta ? (
          // Lista vacía CON un fallo encima no se puede rotular "no tienes
          // nada": no lo sabemos. El aviso de arriba ya lo explica.
          null
        ) : (
        <View style={styles.emptyState}>
          <Ionicons name="receipt-outline" size={48} color={colors.textSecondary} />
          <Text style={[styles.emptyStateText, { color: colors.textSecondary }]}>
            Todavía no hay movimientos. Cuando recargues o ganes un bono, aparecerá aquí.
          </Text>
        </View>
        )
      ) : (
        movimientos.map((m) => (
          <View
            key={m.id}
            style={{
              flexDirection: 'row', alignItems: 'center', gap: 12,
              backgroundColor: colors.card, borderColor: colors.border, borderWidth: 1,
              borderRadius: 12, padding: 14, marginBottom: 10,
            }}
          >
            <Ionicons
              name={m.tipo === 'BONO' ? 'gift' : m.tipo === 'TARJETA' ? 'card' : 'arrow-down-circle'}
              size={24}
              color={m.estado === 'APPROVED' || m.estado === 'ACREDITADO' ? '#22C55E' : '#F59E0B'}
            />
            <View style={{ flex: 1 }}>
              <Text style={{ fontWeight: '700', color: colors.text }}>{m.concepto}</Text>
              <Text style={{ fontSize: 12, color: colors.textSecondary, marginTop: 2 }}>
                {m.fecha} · {m.estadoTexto}
              </Text>
            </View>
            <Text style={{ fontWeight: '800', fontSize: 15, color: colors.text }}>
              {pesos(m.monto || 0)}
            </Text>
          </View>
        ))
      )}
    </ScrollView>
  );

  const renderRechargeSelect = () => (
    <ScrollView contentContainerStyle={styles.scrollContent}>
      {/* ⚠️ SEGUNDO CANDADO, Y NO SOBRA.
          Esconder el botón de la portada cierra el único camino que hay HOY.
          Pero a esta pantalla se entra con `setViewState('RECHARGE_SELECT')`,
          y el día que alguien añada otra llamada —desde un aviso, un enlace
          profundo o el panel— las cuentas bancarias volverían a salir en un
          iPhone sin que nadie se diera cuenta. Un rechazo por 3.1.1 no avisa
          con antelación: llega con la app ya enviada. Así que la pantalla se
          defiende sola. */}
      {!COBROS_EN_LA_APP ? (
        <>
          <TouchableOpacity style={styles.backButton} onPress={() => setViewState('HOME')}>
            <Ionicons name="arrow-back" size={24} color={colors.text} />
            <Text style={{ color: colors.text, fontSize: 16, fontWeight: '600' }}>Volver</Text>
          </TouchableOpacity>
          <Text style={[styles.headerTitle, { color: colors.text }]}>Tu membresía</Text>
          <Text style={[styles.headerSubtitle, { color: colors.textSecondary }]}>
            Tu saldo y tu membresía se gestionan desde tu cuenta de Go Safely.
          </Text>
        </>
      ) : (
      <>
      <TouchableOpacity 
        style={styles.backButton} 
        onPress={() => setViewState('HOME')}
      >
        <Ionicons name="arrow-back" size={24} color={colors.text} />
        <Text style={{ color: colors.text, fontSize: 16, fontWeight: '600' }}>Volver</Text>
      </TouchableOpacity>

      <Text style={[styles.headerTitle, { color: colors.text }]}>Elige tu banco</Text>
      {/* EL TEXTO CAMBIA CON QUIÉN ESTÁ MIRANDO.
          Hablar de "membresía mensual para trabajar" a un pasajero es pedirle
          dinero por algo que no existe para él. Solo se dice cuando el
          documento de Firestore confirma que sí paga cuota. */}
      <Text style={[styles.headerSubtitle, { color: colors.textSecondary }]}>
        {mostrarMembresia
          ? (puedePagarConTarjeta
              ? 'Paga tu membresía mensual para trabajar. Con tarjeta se acredita en cuanto Stripe confirma el pago; sin tarjeta, deposita o transfiere el monto y sube tu comprobante.'
              : 'Paga tu membresía mensual para trabajar: deposita o transfiere el monto y sube tu comprobante para acreditarlo.')
          : 'Deposita o transfiere a una de estas cuentas y sube el comprobante. Acreditamos a tu saldo exactamente lo que muestre el comprobante.'}
      </Text>

      {/* CUÁNTO HAY QUE DEPOSITAR — fuera de cualquier bandera.
          Antes esta cifra vivía SOLO dentro de la tarjeta de pago con tarjeta,
          que está apagada por PLATFORM_CARD_ENABLED. O sea: en la única vía de
          pago habilitada, el chofer veía los números de cuenta pero NUNCA el
          monto. Depositar de menos deja la cuota sin cubrir y no puede trabajar;
          depositar de más es dinero suyo parado.

          Y la cifra es la que el SERVIDOR va a cobrar: cuota (propia si la
          tiene) menos el crédito acumulado. Pedir la cuota entera cuando el
          crédito ya cubre parte es cobrarle dos veces lo mismo. */}
      {mostrarMembresia && (
        <View style={[styles.montoBox, { borderColor: colors.accent }]}>
          {/* LAS TRES CIFRAS, SEPARADAS Y A LA VISTA.
              Antes solo se pintaba el resultado. Un chofer al que le abonaron
              una compensación no tenía cómo comprobar que se la habían
              descontado: veía una cifra sola y tenía que creérsela. */}
          <View style={styles.montoFila}>
            <Text style={[styles.montoFilaLabel, { color: colors.textSecondary }]}>
              {cargoAbierto !== null ? 'Lo que debes de tu membresía' : 'Tu membresía del mes'}
            </Text>
            <Text style={[styles.montoFilaValor, { color: colors.text }]}>
              {pesos(deudaDelMes!)}
            </Text>
          </View>
          <View style={styles.montoFila}>
            <Text style={[styles.montoFilaLabel, { color: colors.textSecondary }]}>
              Tu saldo a favor
            </Text>
            <Text style={[styles.montoFilaValor, { color: saldoAplicado > 0 ? '#22C55E' : colors.textSecondary }]}>
              {saldoAplicado > 0 ? `− ${pesos(saldoAplicado)}` : 'RD$ 0'}
            </Text>
          </View>
          {billeteraAplicada > 0 && (
            <View style={styles.montoFila}>
              <Text style={[styles.montoFilaLabel, { color: colors.textSecondary }]}>
                Ya depositado en tu billetera
              </Text>
              <Text style={[styles.montoFilaValor, { color: '#22C55E' }]}>
                − {pesos(billeteraAplicada)}
              </Text>
            </View>
          )}
          <View style={[styles.montoSeparador, { backgroundColor: colors.border }]} />
          <Text style={[styles.montoLabel, { color: colors.textSecondary }]}>
            Deposita o transfiere exactamente
          </Text>
          <Text style={[styles.montoValor, { color: colors.accent }]}>
            {pesos(montoAPagar!)}
          </Text>
          <Text style={[styles.montoNota, { color: colors.textSecondary }]}>
            {saldoAplicado + billeteraAplicada > 0
              ? 'Tu membresía menos lo que ya tienes ganado. Es exactamente la cifra que el sistema te va a cobrar este mes: ni un peso más. Sube el comprobante para acreditarla.'
              : 'Es tu membresía mensual para trabajar, según tu vehículo. Después sube el comprobante para que te la acreditemos.'}
          </Text>
        </View>
      )}

      {/* El crédito le cubre el mes completo: no hay nada que depositar y
          decirle que deposite RD$ 0 sería absurdo. Puede seguir recargando su
          saldo si quiere, con las cuentas de abajo. */}
      {esChoferConCuota && montoAPagar === 0 && (
        <View style={[styles.montoBox, { borderColor: '#22C55E' }]}>
          <Text style={[styles.montoLabel, { color: colors.textSecondary }]}>
            Este mes no tienes que depositar nada
          </Text>
          <View style={[styles.montoFila, { marginTop: 10 }]}>
            <Text style={[styles.montoFilaLabel, { color: colors.textSecondary }]}>
              {cargoAbierto !== null ? 'Lo que debes de tu membresía' : 'Tu membresía del mes'}
            </Text>
            <Text style={[styles.montoFilaValor, { color: colors.text }]}>
              {pesos(deudaDelMes ?? 0)}
            </Text>
          </View>
          <View style={styles.montoFila}>
            <Text style={[styles.montoFilaLabel, { color: colors.textSecondary }]}>
              Tu saldo a favor
            </Text>
            <Text style={[styles.montoFilaValor, { color: '#22C55E' }]}>
              − {pesos(saldoAplicado)}
            </Text>
          </View>
          {billeteraAplicada > 0 && (
            <View style={styles.montoFila}>
              <Text style={[styles.montoFilaLabel, { color: colors.textSecondary }]}>
                Ya depositado en tu billetera
              </Text>
              <Text style={[styles.montoFilaValor, { color: '#22C55E' }]}>
                − {pesos(billeteraAplicada)}
              </Text>
            </View>
          )}
          <View style={[styles.montoSeparador, { backgroundColor: colors.border }]} />
          <Text style={[styles.montoValor, { color: '#22C55E' }]}>RD$ 0</Text>
          <Text style={[styles.montoNota, { color: colors.textSecondary }]}>
            {/* El excedente NO se pierde ni se recorta: el servidor lo arrastra
                al mes siguiente (`creditRemaining`). Decírselo evita que crea
                que el sobrante se lo quedó la plataforma. */}
            {bloqueadoPorImpago
              ? 'Lo que tienes a favor ya cubre tu membresía. Tu cuenta se reactiva sola en menos de 24 horas; no hace falta que deposites.'
              : saldoSobrante > 0
              ? `Tu saldo a favor cubre la membresía completa y te quedan ${pesos(saldoSobrante)} guardados para el mes que viene. Si aun así quieres depositar, puedes hacerlo a cualquiera de las cuentas de abajo.`
              : 'Tu saldo a favor cubre exactamente la membresía de este mes. Si quieres dejar algo adelantado, puedes depositar a cualquiera de las cuentas de abajo.'}
          </Text>
        </View>
      )}

      {/* PAGAR CON TARJETA (cuenta de Stripe del fundador). Solo para quien paga
          cuota y ya la debe: ver `puedePagarConTarjeta`. El monto que se cobra
          lo calcula el servidor con la misma fórmula que esta cifra. Debajo
          sigue el depósito/transferencia con comprobante. */}
      {/* ── RECARGA CON TARJETA, DE MONTO LIBRE ─────────────────────────────
          Va PRIMERO y sin condiciones: es la vía que no depende de que alguien
          apruebe un comprobante. La de la cuota (abajo) sigue apareciendo solo
          cuando hay algo que pagar. */}
      {CUOTA_TARJETA_ENABLED && (
        <View style={[styles.bankCard, { borderColor: '#6772E5' }]}>
          <View style={[styles.bankHeader, { backgroundColor: '#6772E5' }]}>
            <Ionicons name="card" size={20} color="#FFF" />
            <Text style={styles.bankName}>Recargar con tarjeta</Text>
          </View>
          <View style={styles.bankBody}>
            <Text style={[styles.headerSubtitle, { color: colors.textSecondary, marginBottom: 10 }]}>
              Se acredita a tu saldo en cuanto Stripe confirma el cobro. Mínimo RD$50.
            </Text>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
              <Text style={{ color: colors.text, fontSize: 20, fontWeight: '900' }}>RD$</Text>
              <TextInput
                value={montoRecarga}
                onChangeText={(t) => setMontoRecarga(t.replace(/[^\d]/g, ''))}
                keyboardType="numeric"
                maxLength={6}
                placeholder="50"
                placeholderTextColor={colors.textSecondary}
                style={{
                  flex: 1,
                  borderWidth: 1,
                  borderColor: colors.border,
                  borderRadius: 10,
                  paddingHorizontal: 12,
                  paddingVertical: 10,
                  color: colors.text,
                  fontSize: 18,
                  fontWeight: '700',
                }}
              />
            </View>
            <TouchableOpacity
              onPress={handleRecargaConTarjeta}
              disabled={abriendoRecarga}
              style={{
                marginTop: 12,
                backgroundColor: '#6772E5',
                borderRadius: 12,
                paddingVertical: 14,
                alignItems: 'center',
                opacity: abriendoRecarga ? 0.6 : 1,
              }}
            >
              {abriendoRecarga ? (
                <ActivityIndicator color="#FFF" />
              ) : (
                <Text style={{ color: '#FFF', fontWeight: '800' }}>Pagar con tarjeta</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      )}

      {puedePagarConTarjeta && (
      <View style={[styles.bankCard, { borderColor: '#6772E5' }]}>
        <View style={[styles.bankHeader, { backgroundColor: '#6772E5' }]}>
          <Ionicons name="card" size={20} color="#FFF" />
          <Text style={styles.bankName}>Tarjeta de Crédito o Débito</Text>
        </View>
        <View style={styles.bankBody}>
          {/* ACLARACIÓN FUNDADOR — La recarga = pagar la MEMBRESÍA mensual. El
              monto es la cuota del chofer (según vehículo), NO un valor libre.
              Con tarjeta se cobra al instante; sin tarjeta se paga por depósito
              /transferencia (abajo) subiendo el comprobante. */}
          <View style={{ alignItems: 'center', marginBottom: 12 }}>
            <Text style={[styles.headerSubtitle, { color: colors.textSecondary, marginBottom: 2 }]}>
              {saldoAplicado + billeteraAplicada > 0 ? 'Lo que te falta este mes' : 'Tu membresía mensual'}
            </Text>
            <Text style={{ fontSize: 30, fontWeight: '900', color: '#6772E5' }}>{pesos(montoAPagar!)}</Text>
            <Text style={[styles.headerSubtitle, { color: colors.textSecondary, textAlign: 'center', marginTop: 4 }]}>
              Es tu cuota de servicio para trabajar. Págala con tarjeta o por depósito/transferencia.
            </Text>
          </View>
          <TouchableOpacity
            style={[styles.reportBtn, { backgroundColor: '#6772E5' }]}
            /* Se cobra lo mismo que se le pide depositar: cuota menos crédito. */
            onPress={handleStripePayment}
          >
            <Text style={styles.reportBtnText}>Pagar con tarjeta ({pesos(montoAPagar!)})</Text>
          </TouchableOpacity>
          <Text style={styles.disclaimerText}>
            Pago único en pesos (DOP) en la página segura de Stripe. Go Safely nunca ve el número completo ni el código de seguridad de tu tarjeta; de Stripe solo guarda la confirmación del cobro (la marca, los últimos 4 dígitos y los demás datos que detalla la Política de Privacidad).
          </Text>
        </View>
      </View>
      )}

      <Text style={[styles.headerTitle, { color: colors.text, marginTop: 10, fontSize: 18 }]}>
        {mostrarMembresia ? 'Paga por depósito o transferencia' : 'Deposita o transfiere'}
      </Text>

      {/* NO HAY NINGUNA CUENTA ENCENDIDA.
          Antes este caso era invisible: la app seguía enseñando las cuentas de
          respaldo que trae dentro, el usuario transfería a una cuenta retirada
          y el servidor le rechazaba el comprobante con el dinero ya fuera de su
          banco. Es preferible decirlo. */}
      {BANKS.length === 0 && (
        <View style={[styles.bankCard, { borderColor: colors.textSecondary, padding: 16 }]}>
          <Text style={[styles.bankValue, { color: colors.text, textAlign: 'center' }]}>
            Ahora mismo no hay ninguna cuenta disponible para transferencias.
          </Text>
          <Text style={[styles.disclaimerText, { textAlign: 'center', marginTop: 8 }]}>
            Es temporal. Vuelve a intentarlo en un rato{puedePagarConTarjeta ? ' o paga tu membresía con tarjeta' : ''}.
          </Text>
        </View>
      )}

      {BANKS.map((bank) => (
        <View key={bank.id} style={[styles.bankCard, { borderColor: bank.color }]}>
          <View style={[styles.bankHeader, { backgroundColor: bank.color }]}>
            <Ionicons name={bank.icon as any} size={20} color="#FFF" />
            <Text style={styles.bankName}>{bank.name}</Text>
          </View>
          <View style={styles.bankBody}>
            <View>
              <Text style={styles.bankLabel}>Titular:</Text>
              <Text style={[styles.bankValue, { color: colors.text }]}>{bank.accountName}</Text>
            </View>
            <View style={{ marginTop: 12 }}>
              {/* El tipo REAL de la cuenta (Ahorros / Corriente / Teléfono /
                  Efectivo). Estaba escrito a mano como "Cuenta Corriente" para
                  todas: transferir al tipo equivocado le cuesta el dinero al
                  chofer. */}
              <Text style={styles.bankLabel}>{bank.accountLabel}:</Text>
              <View style={styles.accountNumberRow}>
                <Text style={[styles.accountNumber, { color: colors.primary }]}>{bank.accountNumber}</Text>
                <TouchableOpacity onPress={() => handleCopy(bank.accountNumber, bank.name, bank.accountLabel)}>
                  <Ionicons name="copy-outline" size={24} color={colors.primary} />
                </TouchableOpacity>
              </View>
            </View>
            
            {/* ── LA REFERENCIA ────────────────────────────────────────
                Va ANTES del botón, que es cuando todavía no ha transferido.
                Puesto después sería un reproche en vez de una instrucción. */}
            <View style={[styles.referenciaCaja, { borderColor: colors.accent }]}>
              <Text style={[styles.bankLabel, { marginBottom: 2 }]}>
                Pon esto como REFERENCIA o CONCEPTO al transferir:
              </Text>
              {nombreParaLaReferencia ? (
                <View style={styles.accountNumberRow}>
                  <Text style={[styles.accountNumber, { color: colors.primary, fontSize: 16 }]}>
                    {nombreParaLaReferencia}
                  </Text>
                  <TouchableOpacity
                    onPress={() => handleCopy(nombreParaLaReferencia, 'Referencia', 'tu nombre')}
                    accessibilityRole="button"
                    accessibilityLabel="Copiar mi nombre para la referencia"
                  >
                    <Ionicons name="copy-outline" size={22} color={colors.primary} />
                  </TouchableOpacity>
                </View>
              ) : (
                <Text style={[styles.bankValue, { color: colors.text }]}>
                  Tu nombre completo, igual que en tu cédula.
                </Text>
              )}
              <Text style={styles.disclaimerText}>
                Es lo único que nos deja saber que ese depósito es tuyo. Sin tu nombre, el banco solo
                nos dice que entró dinero y tu recarga tarda más en acreditarse.
              </Text>
            </View>

            <TouchableOpacity 
              style={[styles.reportBtn, { backgroundColor: colors.accent }]}
              onPress={() => {
                setSelectedBank(bank.id);
                setViewState('UPLOAD_RECEIPT');
              }}
            >
              <Text style={styles.reportBtnText}>Ya Transferí (Subir Recibo)</Text>
            </TouchableOpacity>
          </View>
        </View>
      ))}
      </>
      )}
    </ScrollView>
  );

  const renderUploadReceipt = () => (
    <ScrollView contentContainerStyle={styles.scrollContent}>
      <TouchableOpacity 
        style={styles.backButton} 
        onPress={() => setViewState('RECHARGE_SELECT')}
      >
        <Ionicons name="arrow-back" size={24} color={colors.text} />
        <Text style={{ color: colors.text, fontSize: 16, fontWeight: '600' }}>Volver</Text>
      </TouchableOpacity>

      <Text style={[styles.headerTitle, { color: colors.text }]}>Reportar Recarga</Text>
      <Text style={[styles.headerSubtitle, { color: colors.textSecondary }]}>
        Sube la captura de pantalla de tu transferencia para que nuestro equipo valide el saldo.
      </Text>

      {/* Se repite el monto aquí a propósito: quien llega a esta pantalla ya
          cambió de vista y puede haber perdido de vista la cifra. Es el número
          que el revisor va a cotejar contra el estado de cuenta. */}
      {mostrarMembresia ? (
        <View style={[styles.montoBox, { borderColor: colors.accent, marginTop: -8 }]}>
          <Text style={[styles.montoLabel, { color: colors.textSecondary }]}>
            Monto que debiste depositar
          </Text>
          <Text style={[styles.montoValor, { color: colors.accent }]}>
            {pesos(montoAPagar!)}
          </Text>
          <Text style={[styles.montoNota, { color: colors.textSecondary }]}>
            {cargoAbierto !== null ? 'Debes' : 'Tu membresía es de'} {pesos(deudaDelMes ?? 0)} y ya tenías {pesos(aCentavos(saldoAplicado + billeteraAplicada))} a
            favor.
          </Text>
        </View>
      ) : (
        /* Quien no paga cuota deposita el monto que quiera, así que la cifra
           nos la tiene que decir él. Es OPCIONAL: el servidor acepta la
           solicitud sin monto declarado y quien revisa acredita lo que muestre
           el comprobante. Preguntarlo solo acelera el cotejo. */
        <View style={[styles.montoBox, { borderColor: colors.border, marginTop: -8 }]}>
          <Text style={[styles.montoLabel, { color: colors.textSecondary }]}>
            ¿Cuánto depositaste? (opcional)
          </Text>
          <TextInput
            value={montoDeclarado}
            onChangeText={setMontoDeclarado}
            keyboardType="numeric"
            placeholder="Escribe el monto en pesos"
            placeholderTextColor={colors.textSecondary}
            style={{
              width: '100%',
              marginTop: 10,
              paddingVertical: 12,
              paddingHorizontal: 14,
              borderRadius: 10,
              borderWidth: 1,
              borderColor: colors.border,
              color: colors.text,
              fontSize: 18,
              fontWeight: '700',
              textAlign: 'center',
            }}
          />
          <Text style={[styles.montoNota, { color: colors.textSecondary }]}>
            Si lo dejas en blanco no pasa nada: acreditamos lo que diga tu comprobante.
          </Text>
        </View>
      )}

      <TouchableOpacity style={styles.uploadArea} onPress={pickReceipt} activeOpacity={0.8} disabled={submitting}>
        {receipt ? (
          <>
            <Image source={{ uri: receipt.uri }} style={styles.receiptPreview} resizeMode="cover" />
            <Text style={{ color: colors.primary, marginTop: 12, fontWeight: 'bold' }}>Toca para cambiar la captura</Text>
          </>
        ) : (
          <>
            <Ionicons name="cloud-upload" size={48} color={colors.primary} />
            <Text style={{ color: colors.primary, marginTop: 12, fontWeight: 'bold' }}>Toca para subir captura</Text>
          </>
        )}
      </TouchableOpacity>

      <TouchableOpacity
        style={[styles.submitReceiptBtn, { backgroundColor: colors.primary, opacity: (!receipt || submitting) ? 0.6 : 1 }]}
        onPress={handleSubmitReceipt}
        disabled={!receipt || submitting}
      >
        <Text style={styles.submitReceiptBtnText}>{submitting ? 'Validando…' : 'Enviar para Verificación'}</Text>
      </TouchableOpacity>
    </ScrollView>
  );

  const renderPending = () => (
    <View style={[styles.scrollContent, { alignItems: 'center', justifyContent: 'center', flex: 1 }]}>
      <Ionicons name="time" size={80} color="#FF9500" />
      <Text style={[styles.headerTitle, { color: colors.text, textAlign: 'center', marginTop: 20 }]}>
        Comprobante recibido
      </Text>
      {/* NO prometemos "unos minutos": la aprobación la hace una PERSONA
          (functions/src/recargas.ts → resolveRechargeRequest, con 2FA de
          admin), no un proceso automático. Prometer un plazo que la app no
          controla es lo que hace que el chofer vuelva cada cinco minutos
          creyendo que algo se rompió. Lo que SÍ es cierto —y está
          implementado— es el aviso al resolverse (notifyOnRechargeResolved). */}
      <Text style={[styles.headerSubtitle, { color: colors.textSecondary, textAlign: 'center', marginTop: 10, paddingHorizontal: 20 }]}>
        Ya lo tenemos. Alguien de nuestro equipo va a cotejar tu transferencia contra
        el estado de cuenta del banco. Te avisamos en cuanto quede acreditada y el
        saldo aparecerá aquí solo: no hace falta que vuelvas a subirla.
      </Text>
      <TouchableOpacity 
        style={[styles.actionBtn, { backgroundColor: colors.accent, marginTop: 30, paddingHorizontal: 30 }]}
        onPress={() => setViewState('HOME')}
      >
        <Text style={styles.actionBtnText}>Volver al Inicio</Text>
      </TouchableOpacity>
    </View>
  );

  const renderStripeProcessing = () => (
    <View style={[styles.scrollContent, { alignItems: 'center', justifyContent: 'center', flex: 1 }]}>
      <Ionicons name="lock-closed" size={80} color="#6772E5" />
      <Text style={[styles.headerTitle, { color: colors.text, textAlign: 'center', marginTop: 20 }]}>
        Abriendo el pago...
      </Text>
      <Text style={[styles.headerSubtitle, { color: colors.textSecondary, textAlign: 'center', marginTop: 10, paddingHorizontal: 20 }]}>
        Preparamos tu cobro y abrimos la página segura de Stripe. Tus datos de tarjeta van directo a Stripe.
      </Text>
      <TouchableOpacity
        style={[styles.backButton, { marginTop: 24, alignSelf: 'center' }]}
        onPress={() => { cobroCancelado.current = true; setViewState('HOME'); }}
        accessibilityRole="button"
      >
        <Text style={{ color: colors.textSecondary, fontSize: 16, fontWeight: '600' }}>Cancelar</Text>
      </TouchableOpacity>
    </View>
  );

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      {/* Header */}
      <View style={[styles.header, { backgroundColor: colors.primary }]}>
        <TouchableOpacity onPress={() => router.canGoBack() ? router.back() : router.replace('/')} style={styles.headerBtn}>
          <Ionicons name="arrow-back" size={24} color="#FFF" />
        </TouchableOpacity>
        <Text style={styles.headerTitleText}>Mi Billetera</Text>
        <View style={styles.headerBtn} />
      </View>

      {viewState === 'HOME' && renderHome()}
      {viewState === 'RECHARGE_SELECT' && renderRechargeSelect()}
      {viewState === 'UPLOAD_RECEIPT' && renderUploadReceipt()}
      {viewState === 'PENDING_VERIFICATION' && renderPending()}
      {viewState === 'STRIPE_PROCESSING' && renderStripeProcessing()}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    paddingTop: Platform.OS === 'ios' ? 50 : 30,
    paddingBottom: 20,
    paddingHorizontal: 16,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottomLeftRadius: 20,
    borderBottomRightRadius: 20,
  },
  headerBtn: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitleText: {
    color: '#FFF',
    fontSize: 20,
    fontWeight: 'bold',
  },
  scrollContent: {
    padding: 20,
    paddingBottom: 100,
  },
  balanceCard: {
    borderRadius: 20,
    padding: 24,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 8,
    marginBottom: 30,
  },
  balanceLabel: {
    fontSize: 16,
    color: '#829AB1',
    fontWeight: '600',
    marginBottom: 8,
  },
  balanceAmount: {
    fontSize: 40,
    fontWeight: '900',
    marginBottom: 20,
  },
  balanceActions: {
    flexDirection: 'row',
    gap: 12,
  },
  actionBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 12,
    gap: 8,
  },
  actionBtnText: {
    color: '#FFF',
    fontSize: 16,
    fontWeight: 'bold',
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 16,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    backgroundColor: 'rgba(0,0,0,0.02)',
    borderRadius: 16,
  },
  emptyStateText: {
    marginTop: 12,
    fontSize: 14,
    textAlign: 'center',
  },
  avisoFallo: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    borderWidth: 1,
    borderRadius: 12,
    padding: 14,
    marginBottom: 12,
  },
  montoBox: {
    borderWidth: 2,
    borderRadius: 16,
    padding: 16,
    alignItems: 'center',
    marginBottom: 22,
  },
  montoLabel: {
    fontSize: 12,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  montoValor: {
    fontSize: 34,
    fontWeight: '900',
    marginTop: 4,
  },
  montoNota: {
    fontSize: 12,
    textAlign: 'center',
    marginTop: 8,
    lineHeight: 17,
  },
  // Fila "concepto ....... cifra" del desglose de la membresía.
  montoFila: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    marginBottom: 6,
    gap: 12,
  },
  montoFilaLabel: { fontSize: 13, flexShrink: 1 },
  montoFilaValor: { fontSize: 15, fontWeight: '800' },
  montoSeparador: { height: 1, width: '100%', marginTop: 6, marginBottom: 12 },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 20,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: '800',
    marginBottom: 8,
  },
  headerSubtitle: {
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 24,
  },
  bankCard: {
    borderWidth: 2,
    borderRadius: 16,
    overflow: 'hidden',
    marginBottom: 20,
    backgroundColor: '#FFF',
  },
  bankHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    gap: 8,
  },
  bankName: {
    color: '#FFF',
    fontSize: 16,
    fontWeight: 'bold',
  },
  bankBody: {
    padding: 16,
  },
  bankLabel: {
    fontSize: 12,
    color: '#829AB1',
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  bankValue: {
    fontSize: 18,
    fontWeight: '700',
    marginTop: 2,
  },
  referenciaCaja: {
    marginTop: 14,
    marginBottom: 4,
    padding: 12,
    borderRadius: 10,
    borderWidth: 1.5,
    borderStyle: 'dashed',
  },
  accountNumberRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: 'rgba(0, 200, 83, 0.1)',
    padding: 12,
    borderRadius: 8,
    marginTop: 4,
  },
  accountNumber: {
    fontSize: 20,
    fontWeight: '900',
    letterSpacing: 1,
  },
  reportBtn: {
    marginTop: 20,
    padding: 14,
    borderRadius: 10,
    alignItems: 'center',
  },
  reportBtnText: {
    color: '#FFF',
    fontWeight: 'bold',
    fontSize: 15,
  },
  uploadArea: {
    borderWidth: 2,
    borderColor: '#E4E7EB',
    borderStyle: 'dashed',
    borderRadius: 16,
    padding: 40,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 24,
    backgroundColor: '#F5F7FA',
  },
  receiptPreview: {
    width: '100%',
    height: 220,
    borderRadius: 8,
  },
  submitReceiptBtn: {
    padding: 16,
    borderRadius: 12,
    alignItems: 'center',
  },
  submitReceiptBtnText: {
    color: '#FFF',
    fontWeight: 'bold',
    fontSize: 16,
  },
  disclaimerText: {
    fontSize: 11,
    color: '#829AB1',
    textAlign: 'center',
    marginTop: 12,
    fontStyle: 'italic',
  }
});

